import { features } from 'config/features';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import useBeforeUnload from 'hooks/useBeforeUnload';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import apiService from 'services/api';
import courseService from 'services/course';
import userService from 'services/user';
import { assignmentDefaultsFromApi } from 'utils/assignmentDefaults';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { compareCanvasRubrics, hasD2lTextOnlyRubricMismatch } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth } = useAuth();
    const {
        course,
        setAssignments,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
    } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const location = useLocation();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState({
        generatedId: null,
        title: '',
        description: '',
        rubric: null,
        citationFormat: CITATION_SELECT_OPTIONS[0].key,
        contentBankResources: [],
        // feedback
        aiFeedbackEnabled: true,
        feedbackSettings: {
            structure: DEFAULT_FEEDBACK_STRUCTURE,
            // used when structure is rubric or system
            numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
            suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
            languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
            tone: DEFAULT_FEEDBACK_TONE,
            // used when structure is templated
            templated: {},
            // used when structure is custom
            customInstructions: '',
        },
        feedbackCustomContext: '',
        // grading
        aiGradingEnabled: true,
        gradingSettings: null,
        gradingCustomContext: '',
        // student
        aiStudentFeedbackEnabled: false,
        studentFeedbackSettings: {
            structure: DEFAULT_FEEDBACK_STRUCTURE,
            // used when structure is rubric or system
            numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
            suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
            languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
            tone: DEFAULT_FEEDBACK_TONE,
            // used when structure is templated
            templated: {},
            // used when structure is custom
            customInstructions: '',
        },
        studentFeedbackCustomContext: '',
        // inline comments
        aiInlineCommentsEnabled: true,
        inlineCommentsSettings: {
            maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
            commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
            languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
            tone: DEFAULT_INLINE_COMMENTS_TONE,
        },
        inlineCommentsCustomContext: '',
    });
    const appliedPreferencesKey = useRef(null);
    const userId = auth?.user_id;
    const courseId = course?.id || course?.course_id;

    useEffect(() => {
        if (!course || !userId || !courseId || appliedPreferencesKey.current === `${userId}:${courseId}`) return;

        appliedPreferencesKey.current = `${userId}:${courseId}`;
        const preferenceKey = appliedPreferencesKey.current;
        const applyPreferences = async () => {
            let instructorPreferences = null;
            try {
                instructorPreferences = await userService.getAssignmentDefaults(userId);
            } catch (error) {
                // The original product defaults remain the safe fallback.
            }

            const defaults = assignmentDefaultsFromApi(instructorPreferences);
            if (appliedPreferencesKey.current !== preferenceKey) return;
            setForm((current) => ({
                ...current,
                aiGradingEnabled: defaults.aiGradingEnabled,
                gradingCustomContext: defaults.gradingCustomContext,
                aiFeedbackEnabled: defaults.aiFeedbackEnabled,
                feedbackSettings: {
                    ...current.feedbackSettings,
                    ...defaults.feedbackSettings,
                },
                feedbackCustomContext: defaults.feedbackCustomContext,
                aiStudentFeedbackEnabled: defaults.aiStudentFeedbackEnabled,
                studentFeedbackSettings: {
                    ...current.studentFeedbackSettings,
                    ...defaults.studentFeedbackSettings,
                },
                studentFeedbackCustomContext: defaults.studentFeedbackCustomContext,
                aiInlineCommentsEnabled: defaults.aiInlineCommentsEnabled,
                inlineCommentsSettings: {
                    ...current.inlineCommentsSettings,
                    ...defaults.inlineCommentsSettings,
                },
                inlineCommentsCustomContext: defaults.inlineCommentsCustomContext,
            }));
        };

        applyPreferences();
    }, [course, courseId, userId]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectRubricDialog, setSelectRubricDialog] = useState({ open: false });
    const [createRubricDialog, setCreateRubricDialog] = useState({ open: false });
    const [createRubricExistingDialog, setCreateRubricExistingDialog] = useState({ open: false });
    const [createRubricAIDialog, setCreateRubricAIDialog] = useState({ open: false });
    const [editRubricDialog, setEditRubricDialog] = useState({ open: false, customDefaultForm: null });
    const [uploadContentBankResourcesDialog, setUploadContentBankResourcesDialog] = useState({ open: false });
    const [editContentBankResourcesDialog, setEditContentBankResourcesDialog] = useState({ open: false });
    const [gradingSettingsDialog, setGradingSettingsDialog] = useState({ open: false });
    const [feedbackSettingsDialog, setFeedbackSettingsDialog] = useState({ open: false });
    const [studentFeedbackSettingsDialog, setStudentFeedbackSettingsDialog] = useState({ open: false });
    const [inlineCommentsSettingsDialog, setInlineCommentsSettingsDialog] = useState({ open: false });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const validateAll = useCallback(() => {
        let isValid = true;
        let newErrors = {};

        if (!form.title.trim()) {
            isValid = false;
            newErrors = { ...newErrors, title: true };
        }

        if (!form.description.trim()) {
            isValid = false;
            newErrors = { ...newErrors, description: true };
        }

        if (!form.rubric) {
            isValid = false;
            newErrors = { ...newErrors, rubric: true };
        }

        if (errors.gradingSettings) {
            isValid = false;
            newErrors = { ...newErrors, gradingSettings: errors.gradingSettings };
        }

        if (errors.feedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, feedbackSettings: errors.feedbackSettings };
        }

        if (errors.studentFeedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, studentFeedbackSettings: errors.studentFeedbackSettings };
        }

        setErrors(newErrors);

        return {
            isValid,
            errors: newErrors,
        };
    }, [
        errors.feedbackSettings,
        errors.gradingSettings,
        errors.studentFeedbackSettings,
        form.description,
        form.rubric,
        form.title,
    ]);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                history.push('/assignments');
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const publishAssignment = useCallback(
        async (api_data, rubric) => {
            setHasClickedSubmit(true);
            const { isValid, errors } = validateAll();

            if (!isValid) {
                return setDialog({
                    open: true,
                    message: t(
                        'dialog.incomplete-assignment-warning.title',
                        'Please complete all missing required fields before publishing.'
                    ),
                    description: (
                        <ul>
                            {errors.title && (
                                <li style={{ textAlign: 'left' }}>{t('assignment-title-label', 'Assignment title')}</li>
                            )}
                            {errors.description && (
                                <li style={{ textAlign: 'left' }}>
                                    {t(
                                        'dialog.incomplete-assignment-warning.assignment-description',
                                        'Assignment description'
                                    )}
                                </li>
                            )}
                            {errors.rubric && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.assignment-rubric', 'Assignment rubric')}
                                </li>
                            )}
                            {errors.gradingSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                </li>
                            )}
                            {errors.feedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.feedback-settings', 'Feedback settings')}
                                </li>
                            )}
                            {errors.studentFeedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t(
                                        'dialog.incomplete-assignment-warning.formative-feedback-settings',
                                        'Formative feedback settings'
                                    )}
                                </li>
                            )}
                        </ul>
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }

            setIsSaving(true);
            const timestamp = new Date().getTime();
            const assignment = {
                id: uuid(),
                active: true,
                owner_account_id: auth.id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: rubric || form.rubric,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_student_feedback_settings: {
                    structure: form.studentFeedbackSettings.structure,
                    num_suggestions: form.studentFeedbackSettings.numSuggestions,
                    suggestion_length: form.studentFeedbackSettings.suggestionLength,
                    language_level: form.studentFeedbackSettings.languageLevel,
                    tone: form.studentFeedbackSettings.tone,
                    templated: form.studentFeedbackSettings.templated,
                    custom_instructions: form.studentFeedbackSettings.customInstructions,
                },
                ai_student_feedback_context: form.studentFeedbackCustomContext,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: false,
                has_seen_preview: false,
                has_finished_preview: !features.enableFinetuning, // initialize as true when fine tuning is hidden
                preview_files: [],
            };
            if (form?.api_assignment_id) {
                assignment.api_assignment_id = form.api_assignment_id;
                assignment.api_data = api_data || form.api_data;
            }
            const createdAssignment = await courseService.createCourseAssignment(course.id, assignment);
            setAssignments((current) => [...current, createdAssignment]);
            message.success(t('message.create-success', 'Your assignment was successfully created.'));
            history.push('/assignments');
            setLoading(false);
        },
        [
            auth?.id,
            closeDialog,
            course?.id,
            form.aiFeedbackEnabled,
            form.aiGradingEnabled,
            form.aiInlineCommentsEnabled,
            form.aiStudentFeedbackEnabled,
            form.api_assignment_id,
            form.api_data,
            form.citationFormat,
            form.contentBankResources,
            form.description,
            form.feedbackCustomContext,
            form.feedbackSettings,
            form.gradingCustomContext,
            form.gradingSettings,
            form.inlineCommentsCustomContext,
            form.inlineCommentsSettings,
            form.rubric,
            form.studentFeedbackCustomContext,
            form.studentFeedbackSettings,
            form.title,
            history,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            setAssignments,
            setDialog,
            t,
            validateAll,
        ]
    );

    const saveAssignment = useCallback(
        async (api_data, rubric) => {
            setIsSaving(true);
            const timestamp = new Date().getTime();
            const assignment = {
                id: uuid(),
                active: true,
                owner_account_id: auth.id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: rubric || form.rubric,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_student_feedback_settings: {
                    structure: form.studentFeedbackSettings.structure,
                    num_suggestions: form.studentFeedbackSettings.numSuggestions,
                    suggestion_length: form.studentFeedbackSettings.suggestionLength,
                    language_level: form.studentFeedbackSettings.languageLevel,
                    tone: form.studentFeedbackSettings.tone,
                    templated: form.studentFeedbackSettings.templated,
                    custom_instructions: form.studentFeedbackSettings.customInstructions,
                },
                ai_student_feedback_context: form.studentFeedbackCustomContext,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: true,
                has_seen_preview: false,
                has_finished_preview: Boolean(form.api_assignment_id) && !features.enableFinetuning,
                preview_files: [],
            };
            if (form?.api_assignment_id) {
                assignment.api_assignment_id = form.api_assignment_id;
                assignment.api_data = api_data || form.api_data;
            }
            const createdAssignment = await courseService.createCourseAssignment(course.id, assignment);
            setAssignments((current) => [...current, createdAssignment]);
            message.success(t('message.save-success', 'Your assignment was successfully saved.'));
            history.push('/assignments');
        },
        [
            auth?.id,
            course?.id,
            form.aiFeedbackEnabled,
            form.aiGradingEnabled,
            form.aiInlineCommentsEnabled,
            form.aiStudentFeedbackEnabled,
            form.api_assignment_id,
            form.api_data,
            form.citationFormat,
            form.contentBankResources,
            form.description,
            form.feedbackCustomContext,
            form.feedbackSettings,
            form.gradingCustomContext,
            form.gradingSettings,
            form.inlineCommentsCustomContext,
            form.inlineCommentsSettings,
            form.rubric,
            form.studentFeedbackCustomContext,
            form.studentFeedbackSettings,
            form.title,
            history,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            setAssignments,
            t,
        ]
    );

    const onClickSave = useCallback(
        async ({ key }) => {
            if (form.api_assignment_id && process.env.REACT_APP_KIND === 'D2L' && hasD2lTextOnlyRubricMismatch(form)) {
                return setDialog({
                    open: true,
                    message: t('dialog.d2l-textonly-rubric.title', 'Update this rubric in D2L'),
                    description: t(
                        'dialog.d2l-textonly-rubric.description',
                        'Linked text-only rubrics cannot be updated from TimelyGrader. Edit the rubric in D2L, then use Resync from LMS before saving or grading.'
                    ),
                    note: '',
                    variant: 'primary',
                    actionButtonText: t('common:controls.ok', 'OK'),
                    cancelButtonText: '',
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }
            if (key === 'publish') {
                if (!form.api_assignment_id || process.env.REACT_APP_KIND !== 'CANVAS') return publishAssignment();
                const { isValid, errors } = validateAll();
                if (!isValid) {
                    return setDialog({
                        open: true,
                        message: t(
                            'dialog.incomplete-assignment-warning.title',
                            'Please complete all missing required fields before publishing.'
                        ),
                        description: (
                            <ul>
                                {errors.title && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('assignment-title-label', 'Assignment title')}
                                    </li>
                                )}
                                {errors.description && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-description',
                                            'Assignment description'
                                        )}
                                    </li>
                                )}
                                {errors.rubric && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-rubric',
                                            'Assignment rubric'
                                        )}
                                    </li>
                                )}
                                {errors.gradingSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                    </li>
                                )}
                                {errors.feedbackSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.feedback-settings',
                                            'Feedback settings'
                                        )}
                                    </li>
                                )}
                                {errors.studentFeedbackSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.formative-feedback-settings',
                                            'Formative feedback settings'
                                        )}
                                    </li>
                                )}
                            </ul>
                        ),
                        note: '',
                        variant: 'destructive',
                        actionButtonText: t('common:controls.ok', 'Ok'),
                        cancelButtonText: t('common:controls.cancel', 'Cancel'),
                        onAction: () => closeDialog(),
                        onCancel: () => closeDialog(),
                        onClose: () => closeDialog(),
                    });
                }
                setLoading(true);
                setIsSaving(true);
                const apiAssignment = await apiService.getAssignment({
                    course_id: auth.course_id,
                    accessToken: localStorage.getItem('access_token'),
                    assignment_id: form.api_assignment_id,
                });
                const doesRubricMatch = compareCanvasRubrics(form, apiAssignment.rubric, apiAssignment.rubric_settings);
                if (doesRubricMatch) return publishAssignment();
                return setPushRubricDialog({
                    open: true,
                    form,
                    onSave: ({ api_data, rubric }) => {
                        if (rubric) publishAssignment(api_data, rubric);
                    },
                });
            }
            if (key === 'save') {
                if (!form.api_assignment_id || process.env.REACT_APP_KIND !== 'CANVAS') return saveAssignment();
                setLoading(true);
                setIsSaving(true);
                const apiAssignment = await apiService.getAssignment({
                    course_id: auth.course_id,
                    accessToken: localStorage.getItem('access_token'),
                    assignment_id: form.api_assignment_id,
                });
                const doesRubricMatch = compareCanvasRubrics(form, apiAssignment.rubric, apiAssignment.rubric_settings);
                if (!doesRubricMatch && form.rubric) {
                    return setPushRubricDialog({
                        open: true,
                        form,
                        onSave: ({ api_data, rubric }) => {
                            if (rubric) saveAssignment(api_data, rubric);
                        },
                    });
                }
                return saveAssignment();
            }
        },
        [auth.course_id, closeDialog, form, publishAssignment, saveAssignment, setDialog, t, validateAll]
    );

    useEffect(() => {
        if (hasClickedSubmit) {
            validateAll();
        }
    }, [hasClickedSubmit, validateAll]);

    useEffect(() => {
        if (location?.state?.assignment) {
            const { api_assignment_id, api_data, title, description, contentBankResources, rubric, citationFormat } =
                location.state?.assignment;
            setForm((current) => ({
                ...current,
                title,
                description,
                rubric,
                citationFormat,
                contentBankResources,
                api_assignment_id,
                api_data,
            }));
        }
    }, [location?.state]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                loading,
                setLoading,
                form,
                setForm,
                errors,
                setErrors,
                selectRubricDialog,
                setSelectRubricDialog,
                createRubricDialog,
                setCreateRubricDialog,
                createRubricExistingDialog,
                setCreateRubricExistingDialog,
                createRubricAIDialog,
                setCreateRubricAIDialog,
                editRubricDialog,
                setEditRubricDialog,
                uploadContentBankResourcesDialog,
                setUploadContentBankResourcesDialog,
                editContentBankResourcesDialog,
                setEditContentBankResourcesDialog,
                gradingSettingsDialog,
                setGradingSettingsDialog,
                feedbackSettingsDialog,
                setFeedbackSettingsDialog,
                studentFeedbackSettingsDialog,
                setStudentFeedbackSettingsDialog,
                inlineCommentsSettingsDialog,
                setInlineCommentsSettingsDialog,
                pushRubricDialog,
                setPushRubricDialog,
                isSaving,
                setIsSaving,
                onClickBack,
                onClickSave,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
